<script setup lang="ts">
const open = defineModel<boolean>({ default: false });

const props = withDefaults(
  defineProps<{
    modalName: string
    variant?: 'default' | 'fullscreen'
  }>(), {
    variant: 'default',
  },
);

defineSlots<{
  'default': () => unknown
  'actions': () => unknown
  'top-actions': () => unknown
  'top-content': () => unknown
}>();

const dialogElement = shallowRef<HTMLDialogElement | null>(null);

watch(open, (isDialogOpen) => {
  if (!dialogElement.value) return;
  trackEvent({
    event: 'navigation',
    event_name: 'modal_interaction',
    interaction_type: isDialogOpen ? 'open' : 'close',
    modal_name: props.modalName,
  });
  // To activate a “modal mode” (with the backdrop)…
  // …we nee to use “showModal()” method
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog
  if (isDialogOpen) {
    dialogElement.value.showModal();
    // Unfocus first button to avoid focus display on iOS
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
  }
  else dialogElement.value.close();
}, { immediate: true });

function onClick(event: Event) {
  if (event.target !== dialogElement.value) return;
  dialogElement.value?.close();
}

// NOTE: about notifications
// Since <dialog> create their own display stack:
// • we need to display them “inside” the <dialog> element
// • global ones are hidden in app.vue
</script>

<template>
  <div>
    <dialog
      ref="dialogElement"
      class="backdrop:bg-black/50 bg-transparent overflow-clip"
      :class="{
        'max-w-none max-h-none h-screen w-screen': props.variant === 'fullscreen',
      }"
      :open
      @click="onClick"
      @close="open = false"
    >
      <aside
        class="bg-white flex flex-col fixed inset-0 lg:static"
        :class="{
          'lg:rounded lg:max-h-[calc(100vh-4rem)]': props.variant === 'default',
          'min-h-screen': props.variant === 'fullscreen',
        }"
      >
        <header class="p-m grid grid-rows gap-xs">
          <div class="flex justify-between gap-m">
            <IadBtn
              icon
              variant="outlined"
              @click="open = false"
            >
              <Icon name="iad-icon:close" />
            </IadBtn>
            <slot name="top-actions" />
          </div>

          <slot name="top-content" />
        </header>
        <div class="overflow-auto min-h-0 flex-1">
          <slot />
        </div>
        <footer class="flex-none px-m py-s flex items-center justify-center empty:hidden">
          <slot name="actions" />
        </footer>
      </aside>
      <IadNotifications />
    </dialog>
  </div>
</template>

<style scoped>
/* https://developer.mozilla.org/en-US/docs/Web/CSS/transition-behavior */
dialog {
  transition-property: opacity, overlay, display;
  transition-duration: 0.3s;
  transition-behavior: allow-discrete;
}
dialog[open] {
  opacity: 1;
}

/*
  Browser support isn't that good (june 2024)
  => remove transition when not supported
*/
@supports (transition-behavior: allow-discrete) {
  /* Final state of the exit animation */
  dialog {
    opacity: 0;
  }
}

/* Needs to be included after the previous dialog[open]
   rule to take effect, as the specificity is the same */
@starting-style {
  dialog[open] {
    opacity: 0;
  }
}
</style>
